import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { Grid, Tooltip, Typography, ListItemText, makeStyles } from "@material-ui/core";

import UsersTable from "components/UsersTable";
import ConfirmModal from "components/ConfirmModal";
import { UserMultipleRoles } from "data/api";
import { useActiveSystemUsers } from "data/admin/systemUsers";
import { useSnackbar } from "data/snackbar/hooks";
import InviteAdmin from "./InviteAdmin";
import asyncAction from "utils/asyncAction";
import { useProjects } from "data/admin/projects";
import { baliHaiGray } from "styles/colors";

type Props = {
  users: UserMultipleRoles[];
};

const useStyles = makeStyles({
  button: {
    color: baliHaiGray,
    transition: "none",
  },
});

const ActiveUsers: React.FC<Props> = ({ users }) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const [, { inviteAdmin, resendAdminInvite, removeSystemUser }] = useActiveSystemUsers();
  const [confirmRemoveUser, setConfirmRemoveUser] = useState<UserMultipleRoles | null>(null);
  const [{ data: projects }] = useProjects();
  const [, { pushMessage }] = useSnackbar();

  const handleInviteAdmin = useCallback(
    ({ email }) =>
      asyncAction(() => inviteAdmin(email))
        .then(() => {
          pushMessage(t("systemUsers.adminInvitationSent", { email }));
        })
        .catch((error) => {
          if (error.status === 400) {
            pushMessage(t("systemUsers.adminInvitationFailure.alreadyExists", { email }));
          } else {
            pushMessage(t("systemUsers.adminInvitationFailure.default"));
          }
        }),
    [inviteAdmin, pushMessage, t]
  );

  const userIsSoleProjectManager = useCallback(
    (user: UserMultipleRoles) => {
      return projects
        .filter(
          (project) => project.projectManagers!.filter((pm) => pm.id === user.id && pm.isActive === true).length === 1
        )
        .map((project) => project.name);
    },
    [projects]
  );

  return (
    <Grid container direction="column" spacing={2}>
      <InviteAdmin onSubmit={handleInviteAdmin} />
      <ConfirmModal
        open={Boolean(confirmRemoveUser)}
        title={t("systemUsers.remove.confirm.title")}
        message={t("systemUsers.remove.confirm.message")}
        confirmButtonText={t("systemUsers.remove.confirm.ok")}
        onConfirm={() => {
          asyncAction(() => removeSystemUser(confirmRemoveUser!.id!))
            .then(() => pushMessage(t("systemUsers.actions.removeSuccess", { email: confirmRemoveUser!.email })))
            .catch(() => pushMessage(t("systemUsers.actions.removeFailure")))
            .finally(() => setConfirmRemoveUser(null));
        }}
        cancelButtonText={t("systemUsers.remove.confirm.cancel")}
        onCancel={() => setConfirmRemoveUser(null)}
      />
      <Grid item>
        <UsersTable
          isActive
          users={users}
          threeDotMenuProps={(user) => ({
            ariaLabel: t("systemUsers.actions.ariaLabel"),
            items: [
              {
                label: t("systemUsers.actions.resend"),
                shouldRender: !user.isRegistered && user.isAdmin,
                onClick: () => {
                  asyncAction(() => resendAdminInvite(user.email!))
                    .then(() => pushMessage(t("systemUsers.actions.resendSuccess", { email: user.email })))
                    .catch(() => pushMessage(t("systemUsers.actions.resendFailure")));
                },
              },
              {
                label:
                  userIsSoleProjectManager(user).length > 0 ? (
                    <Tooltip
                      placement="bottom"
                      title={
                        <Typography>
                          {t("systemUsers.actions.remove.tooltip", {
                            projects: userIsSoleProjectManager(user).join(", "),
                          })}
                        </Typography>
                      }
                    >
                      <ListItemText>{t("systemUsers.actions.remove.text")}</ListItemText>
                    </Tooltip>
                  ) : (
                    t("systemUsers.actions.remove.text")
                  ),
                className: classnames({
                  [classes.button]: userIsSoleProjectManager(user).length > 0,
                }),
                onClick: () => setConfirmRemoveUser(user),
                disabled: userIsSoleProjectManager(user).length > 0,
              },
            ],
          })}
        />
      </Grid>
    </Grid>
  );
};

export default ActiveUsers;
